import * as React from 'react';
import { adopt } from 'react-adopt';
import { MutationFunc } from 'react-apollo';
import { RouteComponentProps, Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import { ApolloClientFactory } from '../../domain/apolloClientFactory';

import GetDeliveryMethodsQuery from '../../components/Query/DeliveryMethods';
import GetPaymentMethodsQuery from '../../components/Query/PaymentMethods';
import FullVariants from '../../components/Query/Products/FullVariants';
import { Button, Container, Loader, OrderBox, Section, Tabs, Title, Svg, Icon } from '../../modules/modules';
import CartService from '../../services/CartService';
import FlashStore from '../../services/FlashStore';
import CountryService from '../../services/CountryService';
import CartNav from '../../components/CartNav';

import DeliveryMethods from '../../components/DeliveryMethods';
import PaymentMethods from '../../components/PaymentMethods';
import { OrderBasketStore } from '../OrderBasket/OrderBasketStore';
import { removeUnavailableItemsFromCart } from '../../helpers/removeUnavailableItemsFromCart';
import GoogleTagManagerService, { generateBasketItems } from '../../services/GoogleTagManagerService';
import gql from 'graphql-tag';
import { showCutString } from 'helpers/src/showCutString';
import { ApolloError } from 'apollo-client';

interface IPaymentAndShippingState {
  loaded: boolean;
  ordering: boolean;
  terms: boolean;
  errors: {
    terms: boolean;
    payment: boolean;
    delivery: boolean;
  };
  checkOrderCreated?: NodeJS.Timeout;
}

interface IPaymentAndShippingProps extends RouteComponentProps<{ language: string }> {
  createOrder: MutationFunc;
  createPayment: MutationFunc;
}

interface ICartParams {
  ids: Array<any>;
  country?: string;
  currencyId: string;
  render: () => void;
}

const CartParams = adopt({
  deliveryMethods: ({ country, render }: ICartParams) => <GetDeliveryMethodsQuery country={country}>{render}</GetDeliveryMethodsQuery>,
  paymentMethods: ({ country, render }: ICartParams) => <GetPaymentMethodsQuery country={country}>{render}</GetPaymentMethodsQuery>,
  variants: ({ ids, render, currencyId }: ICartParams) => <FullVariants ids={ids} currencyId={currencyId}>{render}</FullVariants>
});

@observer
class PaymentAndShipping extends React.Component<IPaymentAndShippingProps, IPaymentAndShippingState> {

  constructor(props: any) {
    super(props);
    CartService.GetInstance().validateBasketStatus();

    this.state = {
      ordering: false,
      loaded: false,
      terms: false,
      errors: {
        terms: false,
        payment: false,
        delivery: false,
      }
    };

  }

  private onChange = (e: any) => {
    const value = e.target.checked !== undefined
      ? e.target.checked
      : e.target.value;

    this.setState({
      ...this.state, [e.target.name]: value
    });
  }

  private handleChangePaymentMethod = (e: any) => {
    const { name, value } = e.target;

    if (name === 'paymentMethod') {
      if (value) {
        GoogleTagManagerService.addPayment(value);
      }
      CartService.GetInstance().setPayment(value || null);
    }

    this.isValid();
  }

  private handleChangeDeliveryMethod = (e: any) => {
    if (e === null) {
      CartService.GetInstance().setDelivery(null);
      CartService.GetInstance().setDeliveryInsuranceLimit(null);

    } else {
      const { name, value } = e.target;

      if (name === 'deliveryMethod') {
        if (value) {
          GoogleTagManagerService.addShipping(value, CountryService.GetInstance().language);
        }
        CartService.GetInstance().setDelivery(value || null);
        CartService.GetInstance().setDeliveryInsuranceLimit(null);
      }

      if (name === 'deliveryInsurance') {
        CartService.GetInstance().setDeliveryInsuranceLimit(value);
      }
    }

    this.isValid();
  }

  private isValid = async () => {
    const paymentId = CartService.GetInstance().paymentId;
    const deliveryId = CartService.GetInstance().deliveryId;

    this.setState({
      errors: {
        ...this.state.errors,
        payment: !paymentId,
        delivery: !deliveryId
      }
    });

    return (paymentId && deliveryId);
  }

  public async handleSend(paymentMethods: Array<GQL.IPaymentMethod>, deliveryMethods: Array<GQL.IDeliveryMethod>) {
    const countryCode = CountryService.GetInstance().countryCode;
    const language = CountryService.GetInstance().language;
    const currency = CountryService.GetInstance().currency;
    const currencyId = currency ? currency.id : null;
    const countryId = CountryService.GetInstance().country.id;
    const cartService = CartService.GetInstance();

    // pokud nikdo nevybral dopravu a platbu - např. zákazník se po vybrání dopravy vrátil na eshop a spustil nákup znovu
    const selectedDelivery = CartService.GetInstance().delivery;
    if (selectedDelivery) {
      GoogleTagManagerService.addShipping(selectedDelivery, CountryService.GetInstance().language);
    }
    const selectedPayment = CartService.GetInstance().payment;
    if (selectedPayment) {
      GoogleTagManagerService.addPayment(selectedPayment);
    }

    // const validators = this.formValidators;
    const store = CartService.GetInstance();

    const paymentId = store.paymentId;
    const deliveryId = store.deliveryId;

    // vybraný způsob platby
    const paymentMethod = paymentMethods.find((p: GQL.IPaymentMethod) => p.id === paymentId);
    // vybraný způsob dopravy
    const deliveryMethod = deliveryMethods.find((d: GQL.IDeliveryMethod) => d.id === deliveryId);

    if (!paymentMethod || !deliveryMethod) {
      FlashStore.GetInstance().showMessage('Please select a payment method and delivery method.', 'error');
      return;
    }

    const valid = this.isValid();

    const status = await CartService.GetInstance().validateBasketStatus();
    if (!status) {
      return;
    }

    if (false === cartService.doCountryCurrenciesMatch()) {
      this.props.history.push('/personal-data');
      return;
    }

    if (!this.state.terms) {
      FlashStore.GetInstance().showMessage('Before you complete your order, you must agree to the terms of use and processing of your personal information.', 'error');
      return;
    }

    const recycledBox = store.recycledBox;
    const deliveryInsuranceLimit = store.deliveryInsuranceLimit;

    if (!valid || !paymentId || !deliveryId || !currencyId || !status) {
      // FlashStore.GetInstance().showMessage('Prosíme, doplňte chybějící údaje.', 'error', 4000);
      return;
    }

    const values = store.customerData as any;

    const GTMItems = generateBasketItems(CartService.GetInstance().orderItems.map(o => o.variant));

    if (!this.state.ordering) {
      this.setState({ ordering: true }, async () => {
        try {
          const orderData = {
            countryId,
            deliveryMethodId: deliveryMethod.id,
            paymentMethodId: paymentMethod.id,
            recycledBox,
            deliveryInsurance: deliveryInsuranceLimit !== null,
            currencyId,
            email: values.Email,
            phone: values.Phone,
            voucherIds: CartService.GetInstance().voucherIds,
            // firma
            companyInformation: values.CompanyInformation,
            companyName: values.CompanyInformation === true ? values.Company : null,
            companyRegistrationNumber: values.CompanyInformation === true ? values.CompanyRegistrationNumber : null,
            companyVatNumber: values.CompanyInformation === true ? values.CompanyVatNumber : null,
            companyVatNumberValid: values.CompanyInformation === true ? values.CompanyVatNumberValid : false,
            // lokalizace
            language,
            invoiceFirstName: values.Firstname,
            invoiceLastName: values.Lastname,
            invoiceStreet: values.AddressStreet,
            invoiceHouseNumber: values.AddressStreetNumber,
            invoiceAddressLine1: values.AddressLine1,
            invoiceAddressLine2: values.AddressLine2,
            invoiceZip: values.AddressZip,
            invoiceCity: values.AddressCity,
            invoiceCountyState: values.AddressCountyState,
            invoiceCountry: values.AddressCountry,
            // doručovací adresa
            anotherDeliveryAddress: values.AnotherDeliveryAddress,
            deliveryFirstName: values.AnotherDeliveryAddress === true ? values.DeliveryFirstName : values.FirstName,
            deliveryLastName: values.AnotherDeliveryAddress === true ? values.DeliveryLastName : values.LastName,
            deliveryPhone: values.AnotherDeliveryAddress && values.DeliveryPhone ? values.DeliveryPhone : values.Phone,
            deliveryStreet: values.AnotherDeliveryAddress === true ? values.DeliveryAddressStreet : values.AddressStreet,
            deliveryHouseNumber: values.AnotherDeliveryAddress === true ? values.DeliveryAddressStreetNumber : values.AddressStreetNumber,
            deliveryAddressLine1: values.AnotherDeliveryAddress === true ? values.DeliveryAddressLine1 : values.AddressLine1,
            deliveryAddressLine2: values.AnotherDeliveryAddress === true ? values.DeliveryAddressLine2 : values.AddressLine2,
            deliveryZip: values.AnotherDeliveryAddress === true ? values.DeliveryAddressZip : values.AddressZip,
            deliveryCity: values.AnotherDeliveryAddress === true ? values.DeliveryAddressCity : values.AddressCity,
            deliveryCountyState: values.AnotherDeliveryAddress === true ? values.DeliveryAddressCountyState : values.AddressCountyState,
            deliveryCountry: values.DeliveryAddressCity
              ? (values.AnotherDeliveryAddress === true ? values.DeliveryAddressCountry : values.AddressCountry)
              : values.AddressCountry,
            // poznámky
            notes: values.Notes,
            // položky
            items: CartService.GetInstance().orderItems.map(o => {
              return {
                variantId: o.variant.id,
                quantity: o.quantity,
                GTMItemData: GTMItems.items.find(i => i.item_id === o.variant.id),
              };
            })
          };

          const result = await this.props.createOrder({
            variables: {
              order: orderData,
            }
          });

          if (
            Array.isArray(result.data.createOrder.maxAvailableStock) &&
            result.data.createOrder.maxAvailableStock.length > 0
          ) {
            this.afterNotAllItemsAvailableActions(countryCode);
          } else if (result.data.createOrder.queue) {
            const { item, checkInterval } = result.data.createOrder.queue;
            const checkOrderCreated = setInterval(
              (checkItem, checkPaymentMethod, checkLanguage, checkCountryCode: string) => this.checkIfOrderIsCreated(checkItem, checkPaymentMethod, checkLanguage, checkCountryCode),
              checkInterval * 1000,
              item,
              paymentMethod,
              language,
              countryCode
            );
            this.setState({ checkOrderCreated });
          } else {
            const { order } = result.data.createOrder;
            this.afterOrderCreateActions(order, paymentMethod, language);
          }
        } catch (e) {
          if (e instanceof ApolloError && e.graphQLErrors && e.graphQLErrors.length > 0) {
            FlashStore.GetInstance().showMessage(e.graphQLErrors[0].message, 'error', 10000); 
          }
          this.setState({ ordering: false });
        }
      });
    }
  }

  private afterOrderCreateActions(order: any, paymentMethod: any, language: any) {
    CartService.GetInstance().removeEverything();
    OrderBasketStore.ResetInstance();

    const { email, orderNumber } = order;

    this.setState({ ordering: false });

    // platba online
    if (paymentMethod.gateway) {
      this.sendGoogleEcommerce(language, order)
        .then(() => {
          // přesměr na stránku s potvrzením objednávky
          // Country code urls are not used anymore
          // this.props.history.push(`/${countryCode}/order-finished/${orderNumber}/${order.id}`);
          this.props.history.push(`/order-finished/${orderNumber}/${order.id}`);
        });
    } else {
      this.sendGoogleEcommerce(language, order)
        .then(() => {
          // Country code urls are not used anymore
          // this.props.history.push(`/${countryCode}/order-finished/${orderNumber}/${order.id}`, {
          this.props.history.push(`/order-finished/${orderNumber}/${order.id}`, {
            orderNumber,
            email
          });
        });
    }
  }

  private async checkIfOrderIsCreated(queueItemId: string, paymentMethod: any, language: any, countryCode: string) {
    const client = ApolloClientFactory.GetClient();

    const query = gql`
        query GetOrderQueueItem ($itemId: String!) {
          orderQueueItem(itemId: $itemId)
        }`;

    const response = await client.query({
      query,
      variables: { itemId: queueItemId },
      fetchPolicy: 'network-only'
    });

    if (response && response.data && response.data.orderQueueItem) {
      if (response.data.orderQueueItem.order) {
        if (this.state.checkOrderCreated) {
          clearInterval(this.state.checkOrderCreated);
          this.setState({ checkOrderCreated: undefined });
        }
        this.afterOrderCreateActions(response.data.orderQueueItem.order, paymentMethod, language);
      }

      if (response.data.orderQueueItem.processingResult
        && Array.isArray(response.data.orderQueueItem.processingResult.maxAvailableStock)
        && response.data.orderQueueItem.processingResult.maxAvailableStock.length > 0) {
        if (this.state.checkOrderCreated) {
          clearInterval(this.state.checkOrderCreated);
          this.setState({ checkOrderCreated: undefined });
        }
        this.afterNotAllItemsAvailableActions(countryCode, response.data.orderQueueItem.processingResult.maxAvailableStock);
      }
    }
  }

  private afterNotAllItemsAvailableActions(countryCode: string, maxAvailableItems?: {
    productVariantId: string;
    inStock: number;
  }[]) {
    if (Array.isArray(maxAvailableItems)) {
      maxAvailableItems.forEach(maxItem => CartService.GetInstance().updateQuantity(maxItem.productVariantId, maxItem.inStock));
    }
    this.setState({ ordering: false });
    ApolloClientFactory.GetClient().resetStore();
    this.props.history.push(`/${countryCode}/cart`);
  }

  /**
   * Odeslání objednávky do Google Tag Manageru
   *
   * @param {string} language
   * @param order
   * @returns {Promise<any>}
   */
  private sendGoogleEcommerce(language: string, order: any) {
    return new Promise((resolve, reject) => {
      const country = JSON.parse(localStorage.getItem('country') || '') as any;
      const currencyCode = country && country.details && country.details.currency;
      const dataLayer = (window as any).dataLayer;
      if (dataLayer && currencyCode) {
        const products: any[] = [];
        if (order && order.orderedItems) {
          order.orderedItems.forEach((item: any) => {
            products.push({
              id: item.ean,
              sku: item.sku,
              name: `${(item.name[language] ? item.name[language] : item.name.cs)}`,
              price: item.price,
              quantity: item.quantity,
            });
          });
        }

        const data = {
          event: 'Purchase',
          ecommerce: {
            currencyCode,
            purchase: {
              actionField: {
                id: order.orderNumber,
                revenue: order.grandTotal,
                shipping: order.deliveryTotal,
              },
              products
            }
          }
        };

        dataLayer.push(data);
      }

      resolve(null);
    });
  }

  public render() {
    if (this.state.checkOrderCreated) {
      return <Loader />;
    }
    const { country, countryCode, currency } = CountryService.GetInstance();
    const cs = CartService.GetInstance();
    const customerData = cs.customerData as any;
    const deliveryCountry = cs.deliveryCountryByCustomer;

    // firma
    const CompanyInformation = customerData.CompanyInformation;
    const CompanyName = customerData.CompanyInformation === true ? customerData.Company : null;
    const CompanyRegistrationNumber = customerData.CompanyInformation === true ? customerData.CompanyRegistrationNumber : null;
    const CompanyVatNumber = customerData.CompanyInformation === true ? customerData.CompanyVatNumber : null;

    const Phone = customerData.Phone;
    const Email = customerData.Email;
    const InvoiceFirstName = customerData.Firstname;
    const InvoiceLastName = customerData.Lastname;
    const InvoiceStreet = customerData.AddressStreet;
    const InvoiceHouseNumber = customerData.AddressStreetNumber;
    const InvoiceAddressLine1 = customerData.AddressLine1;
    const InvoiceAddressLine2 = customerData.AddressLine2;
    const InvoiceZip = customerData.AddressZip;
    const InvoiceCity = customerData.AddressCity;
    const InvoiceCountyState = customerData.AddressCountyState;
    const InvoiceCountry = customerData.AddressCountry;

    const AnotherDeliveryAddress = customerData.AnotherDeliveryAddress;
    const DeliveryFirstName = customerData.AnotherDeliveryAddress === true ? customerData.DeliveryFirstName : customerData.FirstName;
    const DeliveryLastName = customerData.AnotherDeliveryAddress === true ? customerData.DeliveryLastName : customerData.LastName;
    const DeliveryPhone = customerData.AnotherDeliveryAddress === true && customerData.DeliveryPhone ? customerData.DeliveryPhone : customerData.Phone;
    const DeliveryStreet = customerData.AnotherDeliveryAddress === true ? customerData.DeliveryAddressStreet : customerData.AddressStreet;
    const DeliveryHouseNumber = customerData.AnotherDeliveryAddress === true ? customerData.DeliveryAddressStreetNumber : customerData.AddressStreetNumber;
    const DeliveryAddressLine1 = customerData.AnotherDeliveryAddress === true ? customerData.DeliveryAddressLine1 : customerData.AddressLine1;
    const DeliveryAddressLine2 = customerData.AnotherDeliveryAddress === true ? customerData.DeliveryAddressLine2 : customerData.AddressLine2;
    const DeliveryZip = customerData.AnotherDeliveryAddress === true ? customerData.DeliveryAddressZip : customerData.AddressZip;
    const DeliveryCity = customerData.AnotherDeliveryAddress === true ? customerData.DeliveryAddressCity : customerData.AddressCity;
    const DeliveryCountyState = customerData.AnotherDeliveryAddress === true ? customerData.DeliveryAddressCountyState : customerData.AddressCountyState;
    const DeliveryCountry = customerData.DeliveryAddressCity
      ? (customerData.AnotherDeliveryAddress === true ? customerData.DeliveryAddressCountry : customerData.AddressCountry)
      : customerData.AddressCountry;

    const countryId = deliveryCountry ? deliveryCountry.id : country.id;

    if (CartService.GetInstance().getAllVariantIds().length == 0) {
      this.props.history.push(`/${countryCode}/cart`);
      return null;
    }

    return (
      <main>
        <CartParams
          ids={CartService.GetInstance().getAllVariantIds()}
          country={countryId}
          currencyId={currency.id}
        >
          {({ deliveryMethods, paymentMethods, variants }: any) => {
            if (
              deliveryMethods.deliveryMethods.loading ||
              paymentMethods.paymentMethods.loading ||
              variants.fullVariants.loading
            ) {
              return <Loader />;
            }

            removeUnavailableItemsFromCart(variants, currency);

            // rekalkulace cen pro případ že se změnila měna
            // const cartContent = CartService.GetInstance().getAllVariantIds().map(variantId => {
            //     const quantity = CartService.GetInstance().getQuantityForVariant(variantId);
            //     const itemPrice = findPrice(variants.find((variant: any) => {
            //         return variant.id === variantId;
            //     }).prices);
            //     const recommendedItemPrice = itemPrice;
            //     return {
            //         variant, quantity, itemPrice, recommendedItemPrice
            //     };
            // });
            // for (const item of cartContent) {
            //     CartService.GetInstance().removeItemCompletelyFromCart(item.variantId);
            //     CartService.GetInstance().addToCart(item.variant, item.recommendedItemPrice);
            // }

            // data položek v košíku
            const eans = variants.fullVariants.variants;
            const weightForShipping = Array.isArray(variants.fullVariants.variants) && variants.fullVariants.variants.reduce((wfs: number, variant: GQL.IProductVariant) => {
              return wfs + (variant.product.weightForShipping || variant.product.weight || 0);
            }, 0);

            // dostupný seznam aktivních doprav
            const deliveryMethodsList = deliveryMethods.deliveryMethods.deliveryMethods.filter((paymentMethod: GQL.IDeliveryMethod) => {
              const prices = paymentMethod.prices;
              if (!Array.isArray(prices) || prices.length < 1) {
                return true;
              }

              let maxWeight = 0;
              for (const price of prices) {
                if (price.toWeight === null) {
                  return true;
                }

                maxWeight = Math.max(maxWeight, price.toWeight);
              }

              return weightForShipping <= (maxWeight * 1000);
            });

            // dostupný seznam způsobů platby
            const paymentMethodsList = paymentMethods.paymentMethods.paymentMethods;

            // vybraný způsob platby
            const paymentId = CartService.GetInstance().paymentId;
            const selectedPaymentMethod = paymentId && paymentMethodsList
              ? paymentMethodsList.find((p: GQL.IPaymentMethod) => p.id === paymentId)
              : undefined;

            return (
              <Section type="payment-and-shipping">
                <Container>
                  <div className="cart">
                    <CartNav step={3} />

                    <div className="row">
                      <div className="col-md-12">
                        <Title><h2>Payment and Shipping</h2></Title>
                      </div>
                    </div>

                    {/* {!this.state.loaded && AuthService.GetInstance().loggedIn &&
                      <Loader />} */}

                    <div className="order">

                      <div className="row">
                        <div className="col-md-6">

                          <div className="form__section">
                            <Title type="shopping-cart-subtitle">
                              Payment method
                            </Title>
                            <PaymentMethods
                              fieldName="paymentMethod"
                              selected={CartService.GetInstance().paymentId}
                              paymentMethods={paymentMethodsList}
                              onChange={(e: any) => this.handleChangePaymentMethod(e)}
                              errorMessage={this.state.errors.payment ? 'Please choose payment method' : undefined}
                            />
                          </div>

                          <div className="form__section">
                            <Title type="shopping-cart-subtitle">
                              Shipping method
                            </Title>
                            <DeliveryMethods
                              fieldName="deliveryMethod"
                              selected={CartService.GetInstance().deliveryId}
                              selectedPaymentMethod={selectedPaymentMethod}
                              deliveryMethods={deliveryMethodsList}
                              insuranceLimit={CartService.GetInstance().deliveryInsuranceLimit}
                              onChange={(e: any) => this.handleChangeDeliveryMethod(e)}
                              errorMessage={this.state.errors.delivery ? 'Please choose shiping method' : undefined}
                            />
                          </div>

                          <div className="form__section">
                            <Title type="shopping-cart-subtitle">
                              Packaging preference
                            </Title>
                            <div>
                              <table className="table table--form">
                                <tr>
                                  <td>
                                    <div className="checkbox">
                                      <label className="checkbox__label">
                                        <span>Giving packaging a second life - together! I’d like my order to be packed in a reused/recycled (but still high-quality) box.</span>
                                        <input
                                          checked={CartService.GetInstance().recycledBox}
                                          onChange={() => CartService.GetInstance().toggleRecycledBox()}
                                          className="checkbox__input"
                                          name="terms"
                                          type="checkbox"
                                        />
                                        <div className="checkbox__checkmark" />
                                      </label>
                                    </div>
                                  </td>
                                </tr>
                              </table>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-1" />
                        <div className="col-md-5">
                          {/** recap of addresses */}
                          <div className="orderBox">
                            <div className="orderBox__content">
                              <div className="orderBox__item">
                                {CompanyInformation && (<div className="orderBox__info">
                                  <div className="orderBox__infoItem">
                                    <div className="orderBox__label">
                                      <>
                                        <strong>Address:</strong><br />
                                        {CompanyName ? <>{CompanyName}<br /></> : ''}
                                        {CompanyRegistrationNumber ? <>{CompanyRegistrationNumber}, </> : ''}
                                        {CompanyVatNumber ? <>{CompanyVatNumber}<br /></> : ''}
                                      </>
                                    </div>
                                  </div>
                                </div>)}
                                <div className="orderBox__info">
                                  <div className="orderBox__infoItem">
                                    <div className="orderBox__label">
                                      {!CompanyInformation && <><strong>Address:</strong><br /></>}
                                      {InvoiceFirstName} {InvoiceLastName}<br />
                                      {Phone}, {Email}<br />
                                      {InvoiceStreet} {InvoiceHouseNumber ? <>{InvoiceHouseNumber}<br /></> : ''}
                                      {InvoiceAddressLine1 ? <>{InvoiceAddressLine1}<br /></> : ''}
                                      {InvoiceAddressLine2 ? <>{InvoiceAddressLine2}<br /></> : ''}
                                      {InvoiceCity} {InvoiceZip}<br />
                                      {InvoiceCountyState ? <>{InvoiceCountyState}, </> : ''} {InvoiceCountry}
                                    </div>
                                  </div>
                                </div>
                                {AnotherDeliveryAddress && <div className="orderBox__info">
                                  <div className="orderBox__infoItem">
                                    <div className="orderBox__label">
                                      <strong>Delivery address:</strong><br />
                                      {DeliveryFirstName} {DeliveryLastName}<br />
                                      {showCutString(DeliveryPhone)}<br />
                                      {DeliveryStreet} {DeliveryHouseNumber ? <>{DeliveryHouseNumber}<br /></> : ''}
                                      {DeliveryAddressLine1 ? <>{DeliveryAddressLine1}<br /></> : ''}
                                      {DeliveryAddressLine2 ? <>{DeliveryAddressLine2}<br /></> : ''}
                                      {DeliveryCity} {DeliveryZip}<br />
                                      {DeliveryCountyState ? <>{DeliveryCountyState}, </> : ''} {DeliveryCountry}<br />
                                    </div>
                                  </div>
                                </div>}
                              </div>
                            </div>
                          </div>

                          <OrderBox
                            type={'cart'}
                            title="Summary"
                            products={(eans || []).map((variant: any) => {
                              return {
                                variant,
                                quantity: CartService.GetInstance().getQuantityForVariant(
                                  variant.id
                                ),
                              };
                            })}
                            price={CartService.GetInstance().prices}
                            payment={CartService.GetInstance().payment}
                            delivery={CartService.GetInstance().delivery}
                          />

                          <div>
                            <div className="checkbox">
                              <label className="checkbox__label">
                                <span>I agree to the <a
                                  style={{ textDecoration: 'underline' }}
                                  target="_blank"
                                  href={`https://www.divesoft.com/en/support/terms-and-conditions`}
                                >Terms and Conditions</a>.</span>
                                <input
                                  className="checkbox__input"
                                  name="terms"
                                  defaultChecked={this.state.terms}
                                  type="checkbox"
                                  onClick={this.onChange}
                                />
                                <div className="checkbox__checkmark" />
                              </label>
                              {this.state.errors.terms === true &&
                                <div className="form__error">It is not possible to continue without the terms</div>}
                            </div>
                          </div>
                          <div className="nav-buttons nav-buttons--spaced">
                            {/* Country code urls are not used anymore
                            <Link to={`/${countryCode}/personal-data`} className="btn btn--return btn--fullMd"> */}
                            <Link to={'/personal-data'} className="btn btn--return btn--fullMd">
                              Back
                            </Link>
                            <Button onClick={() => this.handleSend(paymentMethodsList, deliveryMethodsList)} type="proceed" className="fullMd">
                              Place the order
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Container>
              </Section>
            );
          }}
        </CartParams>
      </main>
    );
  }
}

export default PaymentAndShipping;



// WEBPACK FOOTER //
// ./src/pages/PaymentAndShipping/PaymentAndShipping.tsx